import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatDistanceToNow } from 'date-fns';

const RecentRuns = ({ runs, loading, onRefresh, runType }) => {
  const [selectedRun, setSelectedRun] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredRuns = useMemo(() => {
    if (!searchQuery) return runs;
    const searchLower = searchQuery.toLowerCase();
    return runs.filter(run => {
      const runName = run.run_name || run.component_type || '';
      return runName.toLowerCase().includes(searchLower);
    });
  }, [runs, searchQuery]);

  const getStatusBadge = (status) => {
    if (status === 'success') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-50 text-primary-500 border border-primary-500/30">
          Success
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-error-50 text-error border border-error/30">
        Error
      </span>
    );
  };

  const formatCost = (cost) => {
    if (!cost || cost === 0) return '$0.00';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
  };

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (e) {
      return 'Unknown';
    }
  };

  const getRunTypeBadge = (runType) => {
    const styles = {
      execution: 'bg-success-50 text-primary-500 border-primary-500/20',
      generation: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      refinement: 'bg-info-50 text-info border-info/20',
      additional: 'bg-warning-50 text-warning border-warning/20'
    };
    const style = styles[runType] || styles.execution;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${style}`}>
        {runType}
      </span>
    );
  };

  return (
    <div className="rounded-lg overflow-hidden bg-surface-primary/60 border border-border-secondary">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border-secondary">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-text-primary">
            Recent Logs
          </h3>
          <span className="text-xs text-text-tertiary">
            {filteredRuns.length}{searchQuery && ` of ${runs.length}`} total
          </span>
        </div>
        
        {/* Search Input */}
        <div className="relative">
          <svg 
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-text-tertiary" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="Search by run name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg text-sm transition-all focus:outline-none bg-bg-secondary/60 border border-primary-500/15 text-text-primary focus:border-primary-500/30 placeholder:text-text-muted"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors text-text-tertiary hover:text-text-secondary"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden">
        {loading && runs.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-primary-500/20 border-t-primary-500" />
            <p className="mt-4 text-sm text-text-tertiary">Loading runs...</p>
          </div>
        ) : filteredRuns.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-success-50 border-2 border-primary-500/20">
              <svg
                className="h-8 w-8 text-primary-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <h3 className="text-sm font-semibold mb-1 text-text-primary">
              {searchQuery ? 'No matching runs' : 'No runs found'}
            </h3>
            <p className="text-xs text-text-tertiary">
              {searchQuery 
                ? 'Try adjusting your search query'
                : 'Runs will appear here once you execute prompts'
              }
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mt-3 px-3 py-1.5 text-xs font-medium rounded-md transition-all bg-success-50 text-primary-500 border border-primary-500/20 hover:bg-success-100"
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto overflow-x-auto [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-primary-500/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-primary-500">
            <table className="min-w-full border-separate border-spacing-0">
              <thead className="sticky top-0 z-10 bg-bg-secondary/95">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary">
                    Duration
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary">
                    Tokens
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary">
                    Cost
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-text-tertiary border-b border-border-secondary">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredRuns.map((run, index) => (
                  <tr
                    key={run.id}
                    onClick={() => setSelectedRun(run)}
                    className={`cursor-pointer transition-colors hover:bg-primary-500/5 ${
                      index < filteredRuns.length - 1 ? 'border-b border-primary-500/5' : ''
                    }`}
                  >
                    {/* Run Info */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <div className="text-sm font-medium text-text-primary">
                          {runType === 'runs' 
                            ? (run.run_name || 'Untitled')
                            : runType === 'combined'
                              ? (run.run_name || 'Untitled')
                              : (run.component_type || 'Untitled')
                          }
                        </div>
                        <div className="text-xs mt-0.5 text-text-tertiary">
                          {run.model}
                          {runType === 'combined' && run.component_type && (
                            <span className="ml-2">
                              • {run.component_type}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(run.status)}
                    </td>

                    {/* Duration */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-mono text-text-secondary">
                        {run.latency_ms ? `${(run.latency_ms / 1000).toFixed(2)}s` : '-'}
                      </div>
                    </td>

                    {/* Tokens */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-mono text-text-secondary">
                        {run.token_usage?.total_tokens?.toLocaleString() || '-'}
                      </div>
                    </td>

                    {/* Cost */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-sm font-medium font-mono text-primary-500">
                        {formatCost(run.estimated_cost)}
                      </div>
                    </td>

                    {/* Time */}
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="text-xs text-text-tertiary">
                        {formatTimestamp(run.created_at)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Run Details Modal */}
      {selectedRun && (
        <div className="fixed inset-0 z-50 overflow-y-auto" onClick={() => setSelectedRun(null)}>
          <div className="flex items-center justify-center min-h-screen px-4 py-6 text-center">
            <div className="fixed inset-0 transition-opacity bg-bg-primary/80 backdrop-blur-sm" />
            
            <div 
              className="relative inline-block w-full max-w-7xl align-middle text-left overflow-hidden transform transition-all rounded-xl bg-surface-primary/95 border border-primary-500/15 shadow-2xl shadow-primary-500/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 py-4 border-b border-primary-500/15 bg-gradient-to-b from-primary-500/5 to-transparent">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-text-primary">
                      {selectedRun.project_name}
                    </h3>
                    <p className="text-sm mt-0.5 text-text-tertiary">
                      {formatTimestamp(selectedRun.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedRun(null)}
                    className="transition-colors p-2 rounded-md text-text-tertiary hover:bg-success-50 hover:text-primary-500"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="px-6 py-5">
                <div className="grid grid-cols-4 gap-6">
                  {/* Column 1: Run Info */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide mb-3 text-primary-500">Run Info</h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-text-tertiary">Status</dt>
                          <dd className="mt-1">{getStatusBadge(selectedRun.status)}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-text-tertiary">Run ID</dt>
                          <dd className="mt-1 text-sm font-mono truncate text-text-secondary">{selectedRun.id}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-text-tertiary">Run Name</dt>
                          <dd className="mt-1 text-sm text-text-primary">{selectedRun.run_name || 'Untitled'}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-text-tertiary">Model</dt>
                          <dd className="mt-1 text-sm font-mono text-text-secondary">{selectedRun.model}</dd>
                        </div>
                        <div>
                          <dt className="text-xs text-text-tertiary">Project ID</dt>
                          <dd className="mt-1 text-sm font-mono truncate text-text-secondary">{selectedRun.project_id}</dd>
                        </div>
                        {selectedRun.component_type && (
                          <div>
                            <dt className="text-xs text-text-tertiary">Type</dt>
                            <dd className="mt-1 text-sm text-text-primary">{selectedRun.component_type}</dd>
                          </div>
                        )}
                        {selectedRun.run_type && (
                          <div>
                            <dt className="text-xs text-text-tertiary">Category</dt>
                            <dd className="mt-1">{getRunTypeBadge(selectedRun.run_type)}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>

                  {/* Column 2: Performance */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide mb-3 text-primary-500">Performance</h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-text-tertiary">Duration</dt>
                          <dd className="mt-1 text-lg font-bold font-mono text-text-primary">
                            {selectedRun.latency_ms ? `${(selectedRun.latency_ms / 1000).toFixed(2)}s` : '-'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs text-text-tertiary">Latency</dt>
                          <dd className="mt-1 text-sm font-mono text-text-secondary">
                            {selectedRun.latency_ms?.toFixed(2)} ms
                          </dd>
                        </div>
                        {selectedRun.output_length !== undefined && selectedRun.output_length !== null && (
                          <div>
                            <dt className="text-xs text-text-tertiary">Output Length</dt>
                            <dd className="mt-1 text-sm font-mono text-text-secondary">
                              {selectedRun.output_length.toLocaleString()} chars
                            </dd>
                          </div>
                        )}
                        <div>
                          <dt className="text-xs text-text-tertiary">Created At</dt>
                          <dd className="mt-1 text-sm text-text-primary">
                            {new Date(selectedRun.created_at).toLocaleString()}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>

                  {/* Column 3: Tokens */}
                  {selectedRun.token_usage && (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-semibold uppercase tracking-wide mb-3 text-primary-500">Tokens</h4>
                        <dl className="space-y-3">
                          <div>
                            <dt className="text-xs text-text-tertiary">Input Tokens</dt>
                            <dd className="mt-1 text-lg font-bold font-mono text-text-primary">
                              {selectedRun.token_usage.prompt_tokens?.toLocaleString()}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-text-tertiary">Output Tokens</dt>
                            <dd className="mt-1 text-lg font-bold font-mono text-text-primary">
                              {selectedRun.token_usage.completion_tokens?.toLocaleString()}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-text-tertiary">Total Tokens</dt>
                            <dd className="mt-1 text-lg font-bold font-mono text-primary-500">
                              {selectedRun.token_usage.total_tokens?.toLocaleString()}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  )}

                  {/* Column 4: Cost */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wide mb-3 text-primary-500">Cost</h4>
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-xs text-text-tertiary">Total Cost</dt>
                          <dd className="mt-1 text-lg font-bold font-mono text-primary-500">
                            {formatCost(selectedRun.estimated_cost)}
                          </dd>
                        </div>
                        {selectedRun.input_cost !== undefined && selectedRun.input_cost !== null && (
                          <div>
                            <dt className="text-xs text-text-tertiary">Input Cost</dt>
                            <dd className="mt-1 text-sm font-semibold font-mono text-text-secondary">
                              {formatCost(selectedRun.input_cost)}
                            </dd>
                            {selectedRun.estimated_cost > 0 && (
                              <dd className="mt-0.5 text-xs text-text-tertiary">
                                {((selectedRun.input_cost / selectedRun.estimated_cost) * 100).toFixed(1)}%
                              </dd>
                            )}
                          </div>
                        )}
                        {selectedRun.output_cost !== undefined && selectedRun.output_cost !== null && (
                          <div>
                            <dt className="text-xs text-text-tertiary">Output Cost</dt>
                            <dd className="mt-1 text-sm font-semibold font-mono text-text-secondary">
                              {formatCost(selectedRun.output_cost)}
                            </dd>
                            {selectedRun.estimated_cost > 0 && (
                              <dd className="mt-0.5 text-xs text-text-tertiary">
                                {((selectedRun.output_cost / selectedRun.estimated_cost) * 100).toFixed(1)}%
                              </dd>
                            )}
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                </div>
                
                {/* Error Section - Full Width at Bottom */}
                {selectedRun.error_message && (
                  <div className="mt-6 pt-6 border-t border-border-secondary">
                    <h4 className="text-xs font-semibold uppercase tracking-wide mb-3 text-error">Error Details</h4>
                    <div className="rounded p-4 bg-error-50 border border-error/20">
                      <dt className="text-sm font-semibold text-error">{selectedRun.error_type}</dt>
                      <dd className="mt-2 text-sm whitespace-pre-wrap text-text-secondary">
                        {selectedRun.error_message}
                      </dd>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 flex justify-end border-t border-primary-500/15 bg-bg-secondary/50">
                <button
                  onClick={() => setSelectedRun(null)}
                  className="px-4 py-2 text-sm font-medium rounded-md transition-all bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:shadow-glow-lg hover:-translate-y-0.5"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

RecentRuns.propTypes = {
  runs: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  onRefresh: PropTypes.func,
  runType: PropTypes.string
};

export default RecentRuns;
